import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import {
  ColDef,
  ICellRendererParams,
  IServerSideDatasource,
  SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useRef } from "react";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useSnackbar from "../../hooks/useSnackbar";
import useToggle from "../../hooks/useToggle";
import Ellipsis from "../common/Ellipsis";
import { nameof } from "../common/ObjectUtils";
import Dialogs from "./dialogs";
import EquipmentAssociationService, {
  EquipmentAssociationRequests,
} from "./EquipmentAssociationService";
import StyledMui from "./StyledMui";
import { EquipmentAssociationTypes } from "./Types";
import { useHasScope } from "../../../../config/globalConfig";
import AgGridOperationTable from "../common/AgGridOperationTable";

interface Props {
  tenant: string;
  table: {
    totalElements: number;
    datasource: IServerSideDatasource;
    onSelect: (val: EquipmentAssociationTypes.ACRISS) => void;
    selectedAcriss?: EquipmentAssociationTypes.ACRISS;
  };
  updateDatasourceKit: () => void;
  updateDatasourceAcriss: () => void;
  submitCount: number;
}

const AssociationTable = (props: Props) => {
  const hasScope = useHasScope;
  const gridRef = useRef<AgGridReact>(null);

  const [showEdit, toggleEdit] = useToggle();
  const [showDelete, toggleDelete] = useToggle();

  const snackbar = useSnackbar();
  const t = useOperationTranslation();

  const onSelectionChanged = (
    event: SelectionChangedEvent<EquipmentAssociationTypes.ACRISS>
  ) => {
    const selectedItem = event.api.getSelectedRows();
    props.table.onSelect(selectedItem[0]);
  };

  const onOpenEdit = (acriss: EquipmentAssociationTypes.ACRISS | undefined) => {
    acriss && props.table.onSelect(acriss);
    toggleEdit();
  };

  const onDelete = () => {
    if (!props.table.selectedAcriss) return;

    const data: EquipmentAssociationRequests.Edit = {
      equipmentKitsId: [],
      ids: [props.table.selectedAcriss.id],
    };

    EquipmentAssociationService.put.edit(props.tenant, data).then(
      () => {
        props.updateDatasourceKit();
        snackbar.show(
          "success",
          t(
            "vehicleEquipmentAssociation.dialogs.delete.messages.acriss.success"
          )
        );
      },
      () => {
        snackbar.show(
          "error",
          t("vehicleEquipmentAssociation.dialogs.delete.messages.acriss.error")
        );
      }
    );
  };

  const columns: ColDef[] = [
    {
      headerName: t("vehicleEquipmentAssociation.tables.acriss.columns.acriss"),
      field: nameof<EquipmentAssociationTypes.ACRISS>("code"),
      flex: 1,
    },
    {
      headerName: t("vehicleEquipmentAssociation.tables.acriss.columns.label"),
      field: nameof<EquipmentAssociationTypes.ACRISS>("label"),
      flex: 1,
      cellRenderer: (
        params: ICellRendererParams<EquipmentAssociationTypes.ACRISS>
      ) => {
        return (
          <Mui.Tooltip title={params.value}>
            <Ellipsis>{params.value}</Ellipsis>
          </Mui.Tooltip>
        );
      },
    },
    {
      headerName: "",
      maxWidth: 80,
      cellStyle: {
        padding: "0px",
        border: "none",
      },
      cellRenderer: (
        params: ICellRendererParams<EquipmentAssociationTypes.ACRISS>
      ) => {
        return (
          <StyledMui.Stack direction="row" spacing="5px">
            {hasScope("SCOPE_UPDATE_MDVEHICLE_EQUIPMENTKIT") && (
              <Mui.Tooltip
                title={t(
                  "vehicleEquipmentAssociation.tables.acriss.actions.edit"
                )}
              >
                <Mui.IconButton
                  color="accent"
                  onClick={() => onOpenEdit(params.data)}
                >
                  <MuiIcons.Edit />
                </Mui.IconButton>
              </Mui.Tooltip>
            )}

            {hasScope("SCOPE_DELETE_MDVEHICLE_EQUIPMENTKIT") && (
              <Mui.Tooltip
                title={t(
                  "vehicleEquipmentAssociation.tables.acriss.actions.delete"
                )}
              >
                <Mui.IconButton color="accent" onClick={toggleDelete}>
                  <MuiIcons.Delete />
                </Mui.IconButton>
              </Mui.Tooltip>
            )}
          </StyledMui.Stack>
        );
      },
    },
  ];

  useEffect(() => {
    gridRef.current?.api.deselectAll();
  }, [props.submitCount]);

  return (
    <Mui.Box>
      <AgGridOperationTable
        columnDefs={columns}
        serverSideDatasource={props.table.datasource}
        serverSideInfiniteScroll={true}
        cacheBlockSize={10}
        rowModelType="serverSide"
        rowSelection="single"
        onSelectionChanged={onSelectionChanged}
        totalElements={props.table.totalElements}
        emptyState={{
          title: t("vehicleEquipmentAssociation.tables.acriss.emptyState.title"),
          content:t(
            "vehicleEquipmentAssociation.tables.acriss.emptyState.content")
          
        }}
      />


      <Dialogs.EditEquipmentKitAssociation
        tenant={props.tenant}
        open={showEdit}
        onClose={toggleEdit}
        acriss={props.table.selectedAcriss}
        updateDatasourceAcriss={props.updateDatasourceAcriss}
        updateDatasourceKit={props.updateDatasourceKit}
      />

      <Dialogs.DeleteConfirmation
        onClose={toggleDelete}
        open={showDelete}
        onDelete={onDelete}
        title={t("vehicleEquipmentAssociation.dialogs.delete.title.acriss")}
        content={t(
          "vehicleEquipmentAssociation.dialogs.delete.content.acriss"
        ).replace(
          "#textElement",
          `${props.table.selectedAcriss?.code} - ${props.table.selectedAcriss?.label}`
        )}
        deleteBtnText={t(
          "vehicleEquipmentAssociation.dialogs.delete.actions.deleteAcriss"
        )}
      />
    </Mui.Box>
  );
};

export default AssociationTable;
